import React from 'react';
import _ from 'lodash';
import cx from 'classnames';

import { clipString } from 'utils/utils';

import { AddButton, EditIconButton, DeleteIconButton, RefreshIconButton, IconButtonList } from 'components/Button';
import { EmptyItem } from 'components/EmptyItem';
import { StatItemList, StatItem, StatItemMoney, StatItemMoneySmall, StatItemNumber, StatItemPercent, StatItemSeconds, StatItemHours, StatItemMinutes } from 'components/Stats';
import Tooltip from 'components/Tooltip';
import * as Icons from 'components/Svg';
import SwitchInput from 'components/forms/SwitchInput';
import { LoadingStatItem } from 'components/Loading';



// ========================================================================================================================
// ====================
// LINK ITEM
// ====================
export const LinkItem = ({ link, stats, active, className, size, onClick, ...props }) => (
  <div className={cx("g-link-item", className, size)} {...props}>
    <div className="link-info">
      <div className="name">{link?.name}</div>
      <div className="destination" title={link?.url}>{clipString(link?.url, 35)}</div>
    </div>
    <StatItemList>
      <StatItemMoneySmall label="Media Value" value={stats?.clicksMediaValue} />
      <StatItemNumber label="Clicks" value={stats?.clicks} />  
      <StatItemNumber label="Chat Messages" value={stats?.messageCount} />
      <StatItemNumber label="Link Views" value={stats?.messageViews} />
      <StatItemPercent label="CTR" value={stats?.linkCtr} />
    </StatItemList>
    <Tooltip tooltip={active ? "Currently serving" : "Not serving from any campaign"}>
      <div className={cx("status", { active })} />
    </Tooltip>
  </div>
)
export default LinkItem;


export const LinkItemEmpty = ({ onAdd, className, size, ...props }) => (
  <EmptyItem className={cx(className, size)}>
    <h3>Create Links to track clicks across channels</h3>
    <h4>Enable the Chatbot and the links will be posted automatically for you in chat</h4>
    <AddButton onClick={onAdd}>Add Link</AddButton>
  </EmptyItem>)


// ====================
// LINK ITEM LIST
// ====================
export const LinkList = ({ links, linkStats, activeLinks, onLinkClick, loading, loadingCount = 4, className, size, align, ...props }) => {
  if(!loading && _.isEmpty(links)) 
    return (
      <EmptyItem>
        <p>No links were served for this time period</p>
      </EmptyItem>);
  
  return (
    <div className={cx("g-link-list", className, size, align)}>
      <div className="list-wrapper">
        { loading && 
          _.map(_.fill(Array(loadingCount), '0'), (_, idx) => <LoadingStatItem key={idx} />) }

        { !loading &&_.map(links, link =>
          <LinkItem
            key={link._id}
            link={link}
            stats={linkStats?.[link._id]}
            active={_.includes(activeLinks, link._id)}
            size={size}
            onClick={onLinkClick}
            {...props} />)}
      </div>
    </div>)
}
  

// ========================================================================================================================
// ====================
// LINK MANAGE ITEM
// ====================
export const LinkManageItem = ({ link, className, size, onEdit, onDelete, onRestore, onSettingsChanged, ...props }) => {
  let onChange = (...args) => onSettingsChanged?.(link._id, ...args);
  return (
    <div className={cx("g-link-manage-item", className, size)} {...props}>
      <div className="link-info">
        <div className="name">{link?.name}</div>
        <a className="destination" href={link?.url} target='_blank'>{clipString(link?.url, 35)} <Icons.ExternalIcon size="s" className="right" /></a>
      </div>
      <div className="manage-actions">
        <IconButtonList>
          { onEdit &&
          <Tooltip tooltip={`Edit Link`}>
            <EditIconButton size="m" onClick={() => onEdit(link._id, link)} />
          </Tooltip>}
          { onDelete &&
          <Tooltip tooltip={`Archive this link`}>
            <DeleteIconButton size="m" onClick={() => onDelete(link._id, link)} />
          </Tooltip>}
          { onRestore &&
          <Tooltip tooltip={`Restore this link back to your library`}>
            <RefreshIconButton size="m" onClick={() => onRestore(link._id, link)} />
          </Tooltip>}
        </IconButtonList>
      </div>
  </div>
  )
}


// ====================
// LINK MANAGE LIST
// ====================
export const LinkManageList = ({ links, loading, onEdit, onDelete, onRestore, onLinkClick, className, size, align, emptyText, ...props }) => {
  emptyText = emptyText ?? "Your account does not have any links yet";
  if(_.isEmpty(links)) {
    return (
      <EmptyItem>
        <p>{emptyText}</p>
      </EmptyItem>);
  }
  return (
    <div className={cx("g-link-manage-list", className, size, align)}>
      <div className="list-wrapper">
        { loading && 
          _.map(_.fill(Array(loadingCount), '0'), (_, idx) => <LoadingStatItem key={idx} />) }

        { !loading &&_.map(links, link =>
          <LinkManageItem
            key={link._id}
            link={link}
            size={size}
            onEdit={onEdit}
            onClick={onLinkClick}
            onDelete={onDelete}
            onRestore={onRestore}
            {...props} />)}
      </div>
    </div>)
}
  


// ========================================================================================================================
// ====================
// LINK TABLE ROW
// ====================
export const LinkRow = ({ link, settings, className, size, onEdit, onDelete, onSettingsChanged, ...props }) => {
  let onChange = (...args) => onSettingsChanged?.(link._id, ...args);
  return (
    <tr
      className={cx("g-link-row", className, size)}
      {...props}>
      <td className="name">{link?.name}</td>
      <td className="destination">{clipString(link?.url, 25)}</td>
      <td className="settings">
        <div>
          <SwitchInput
            name="chatEnabled"
            value={settings.chatEnabled}
            onChange={onChange} />
        </div>
      </td>
      <td className="actions">
        <IconButtonList>
          {onEdit &&
          <EditIconButton size="m" onClick={() => onEdit(link._id, link)} />}
          {onDelete &&
          <DeleteIconButton size="m" onClick={() => onDelete(link._id, link)} />}
        </IconButtonList>
      </td>
    </tr>
  )
}





// ====================
// LINK TABLE
// ====================
export const LinkTable = ({ links, linkSettings, header = true, onEdit, onDelete, onAdd, className, size, align, ...props }) => {
  if(_.isEmpty(links)) {
    return <LinkItemEmpty onAdd={onAdd} />;
  }
  return (
    <table
      className={cx("g-link-table", className, size, align)}>
      {header &&
      <thead>
        <tr className="g-link-table-header">
          <th className="name">Name</th>
          <th className="destination">Destination</th>
          <th className="settings">Chat Enabled</th>
          <th className="actions"></th>
        </tr>
      </thead>}
      <tbody className="list-wrapper">
        {_.map(links, link => (
          <LinkRow
            key={link._id}
            link={link}
            size={size}
            settings={linkSettings?.[link._id]}
            onEdit={onEdit}
            onDelete={onDelete}
            {...props} />))}
      </tbody>
    </table>)
}
  